// Illustrative day-ahead schedule for the hero: 24 hourly price bars colored
// by the planned battery action. Not a live chart — static, clearly labeled
// illustrative, aria-described. Colors #018ff4 (charge) / #d97706 (discharge)
// are palette-validated for both light and dark surfaces; idle hours are
// recessive context in border-gray. One axis (price), thin rounded bars,
// 2px gaps, legend + direct labels so identity is never color-alone.
import { useTranslation } from "react-i18next";

const CHARGE = "#018ff4";
const DISCHARGE = "#d97706";

// [relative price 0..1, action] per hour — a typical day-ahead shape:
// cheap night valley, morning peak, midday solar dip, steep evening peak.
type Action = "c" | "d" | "i";
const HOURS: [number, Action][] = [
  [0.38, "i"], [0.3, "c"], [0.24, "c"], [0.2, "c"], [0.22, "c"], [0.3, "c"],
  [0.52, "i"], [0.78, "d"], [0.88, "d"], [0.7, "d"], [0.5, "i"], [0.36, "i"],
  [0.28, "c"], [0.24, "c"], [0.3, "c"], [0.42, "i"], [0.55, "i"], [0.72, "i"],
  [0.9, "d"], [1.0, "d"], [0.92, "d"], [0.68, "d"], [0.5, "i"], [0.4, "i"],
];

const W = 480;
const H = 190;
const PAD = { top: 34, right: 10, bottom: 24, left: 10 };
const PLOT_H = H - PAD.top - PAD.bottom;
const STEP = (W - PAD.left - PAD.right) / HOURS.length;
const GAP = 2;

export function HeroVisual() {
  const { t } = useTranslation();
  const fill = (a: Action) =>
    a === "c" ? CHARGE : a === "d" ? DISCHARGE : "rgb(var(--c-border))";
  return (
    <figure className="card p-4 w-full max-w-md mx-auto" aria-label={t("landing.heroVisual.caption")}>
      <figcaption className="flex items-center justify-between text-xs text-muted mb-2">
        <span>{t("landing.heroVisual.title")}</span>
        <span className="font-mono text-good">{t("landing.heroVisual.result")}</span>
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img"
           aria-label={t("landing.heroVisual.caption")}>
        {/* direct labels above the two action clusters */}
        <text x={PAD.left + 3.5 * STEP} y={PAD.top - 18} textAnchor="middle"
              fontSize="11" fill={CHARGE}>
          {t("landing.heroVisual.charge")} ↓
        </text>
        <text x={PAD.left + 19.5 * STEP} y={PAD.top - 18} textAnchor="middle"
              fontSize="11" fill={DISCHARGE}>
          {t("landing.heroVisual.discharge")} ↑
        </text>
        {HOURS.map(([p, a], i) => {
          const h = Math.max(4, p * PLOT_H);
          return (
            <rect
              key={i}
              x={PAD.left + i * STEP + GAP / 2}
              y={PAD.top + PLOT_H - h}
              width={STEP - GAP}
              height={h}
              rx={3}
              fill={fill(a)}
              opacity={a === "i" ? 0.9 : 1}
            />
          );
        })}
        {/* recessive baseline + hour ticks */}
        <line x1={PAD.left} y1={PAD.top + PLOT_H + 1} x2={W - PAD.right}
              y2={PAD.top + PLOT_H + 1} stroke="rgb(var(--c-border))" strokeWidth="1" />
        {[0, 6, 12, 18].map((hr) => (
          <text key={hr} x={PAD.left + hr * STEP + STEP / 2} y={H - 8}
                fontSize="10" fill="rgb(var(--c-muted))">
            {String(hr).padStart(2, "0")}
          </text>
        ))}
      </svg>
      <div className="flex items-center gap-4 mt-2 text-xs text-muted flex-wrap">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHARGE }} />
          {t("landing.heroVisual.charge")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: DISCHARGE }} />
          {t("landing.heroVisual.discharge")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-border" />
          {t("landing.heroVisual.idle")}
        </span>
        <span className="ml-auto italic">{t("landing.heroVisual.caption")}</span>
      </div>
    </figure>
  );
}
